import React from 'react'
import { useSearchParams } from 'react-router'

/** Enables proper browser back/forward navigation by preserving history.
 *
 *  Usage requires supplied generic for maximum type safety:
 *   ie. `useSearchParamState<StringLiteralUnion>(..)` */
export const useSearchParamState = <T extends string>(state: {
  key: string
  values: T[]
  defaultValue: T
  /** Sets the default value in URL on entry */
  shouldSetValueOnMount?: boolean
}): [T, (newValue: T, opts?: SetSearchParamsOptions) => void] => {
  const hasSetEntrySearchParamRef = React.useRef(false)
  const { key, values, defaultValue, shouldSetValueOnMount } = state

  const [searchParams, setSearchParams] = useSearchParams({ [key]: defaultValue })

  const currentValue = searchParams.get(key) as (typeof values)[0]
  const isValidValue = values.includes(currentValue)
  const value = isValidValue ? currentValue : defaultValue

  const setValue = React.useCallback(
    (value: (typeof values)[0], opts: SetSearchParamsOptions = {}) => {
      setSearchParams(
        prevParams => {
          const newParams = new URLSearchParams(prevParams)
          opts.paramsToDelete?.forEach(paramToDelete => {
            newParams.delete(paramToDelete)
          })
          newParams.set(key, value)
          return newParams
        },
        { replace: !!opts.shouldReplace }
      )
    },
    [setSearchParams, key]
  )

  // Sets default search param on URL on entry option
  React.useEffect(() => {
    if (!shouldSetValueOnMount || hasSetEntrySearchParamRef.current) return
    if (currentValue === defaultValue) {
      hasSetEntrySearchParamRef.current = true
      setValue(defaultValue, { shouldReplace: true })
    }
  }, [shouldSetValueOnMount, currentValue, defaultValue, setValue])

  // Reset URL to default if value is not recognised
  React.useEffect(() => {
    if (currentValue !== value) {
      setValue(value, { shouldReplace: true })
    }
  }, [setValue, currentValue, value])

  return [value, setValue]
}

// =====
// Types
// =====

type SetSearchParamsOptions = {
  shouldReplace?: boolean
  paramsToDelete?: string[]
}
